import {getButtonsForModule, prepareDefaultMail, getUsersWithAuthorization, getOngoingLiberalitiesSummary, formatReliefFundAmount} from "./utils"
import {refundApplicationStatusObjects, reliefFundFormLabels} from '../staticEntities'
import _ from "lodash";
import Errors from "../../../utils/Errors"
import {decrypt} from "../../../utils/crypto"
import {basicContext} from "../../../utils/contextUtils"
import async from "async"
import moment from "moment"
import {getCNDANotificationData} from "../utils/notifications"

export const entities = [
    {
        // Montant accordé pour un couple « Type sollicitation - Forme d'aide »
        name: 'NeedAllocation',
        fields: [
            {path: 'form', type: 'Form'},
            {path: 'awardedAmount', type: 'decimal', default: 0},
            {
                path: 'formName',
                fieldPath: ['form.id'],
                f: function () {
                    return reliefFundFormLabels[_.get(this, 'form.id')] || ''
                }
            },
            {
                path: 'fullName',
                fieldPath: ['form.id', 'need.nameWithUnit'],
                f: function () {
                    return `${_.get(this, 'need.nameWithUnit', '')} - ${reliefFundFormLabels[_.get(this, 'form.id')] || ''}`
                }
            },
            {
                path: 'awardedAmountInEuros',
                type: 'decimal',
                fieldPath: ['awardedAmount', 'need.natureOfHelp.unit.id'],
                f: function () {
                    return _.get(this, 'need.natureOfHelp.unit.id') === 'euro' ? (this.awardedAmount || 0) : 0
                }
            },
            {
                path: 'awardedAmountInKilograms',
                type: 'decimal',
                fieldPath: ['awardedAmount', 'need.natureOfHelp.unit.id'],
                f: function () {
                    return _.get(this, 'need.natureOfHelp.unit.id') === 'kilogram' ? (this.awardedAmount || 0) : 0
                }
            }
        ],
        filters: [
            {
                name: 'byReliefFund',
                isDefault: false,
                async: true,
                query: (context, callback) => {
                    const reliefFundId = _.get(context, 'data.reliefFund.id')
                    if (!reliefFundId) return callback(null, {_id: null})
                    global.app.C.Need.collection.find({reliefFund: new global.ObjectID(reliefFundId)}, {projection: {_id: 1}})
                        .toArray()
                        .then(needs => callback(null, {need: {$in: needs.map(need => need._id)}}))
                        .catch(callback)
                }
            },
            {
                name: 'hasAwardedAmount',
                isDefault: false,
                query: () => ({awardedAmount: {$gt: 0}})
            }
        ]
    },
    {
        name: 'Need',
        facets: [
            {name: 'files', path: 'supportingDocuments'},
            {name: 'files', path: 'landTypeProof'},
        ],
        fields: [
            {path: 'natureOfHelp',type: 'NatureOfHelp'},
            {path: 'landType', type: 'LandType', nullable: true},
            {
                path: 'name',
                fieldPath: ['natureOfHelp.name'],
                f: function() {
                    return this.natureOfHelp?.name
                }
            },
            {
                path: 'nameWithUnit',
                fieldPath: ['natureOfHelp.name', 'natureOfHelp.unit.name'],
                f: function() {
                    return this.natureOfHelp?.name + ' (' + this.natureOfHelp?.unit?.name + ')'
                }
            },
            // Remplacé par allocations (ventilation par forme d'aide), conservé pour l'historique
            {path: 'form', type: 'Form', nullable: true},
            {path: 'allocations', type: 'NeedAllocation', link: {type: 'OTM', deleteType: 'cascade', onParent: true, onChild: true}},
            {path: 'totalAmountInEuros', type: 'decimal', nullable: true},
            {path: 'totalAmountInKilograms', type: 'decimal', nullable: true},
            {path: 'requestedAmountInEuros', type: 'decimal', nullable: true},
            {path: 'requestedAmountInKilograms', type: 'decimal', nullable: true},
            {
                path: 'awardedAmountInEuros',
                type: 'decimal',
                nullable: true,
                ps: {
                    object: [{
                        $u: function (value) {
                            if (!value) return 0;
                            return value;
                        }
                    }]
                }
            },
            {
                path: 'awardedAmountInKilograms',
                type: 'decimal',
                nullable: true,
                ps: {
                    object: [{
                        $u: function (value) {
                            if (!value) return 0;
                            return value;
                        }
                    }]
                }
            },
            {
                path: 'validatedAmountInEuros',
                fieldPath: ['reliefFund.payments.status.id', 'reliefFund.payments.proposedAmountInEuros', 'reliefFund.payments.need.id'],
                type: 'decimal',
                $f: function (need, context, callback) {
                    const total = need.reliefFund.payments
                        .filter(payment => {
                            const isCurrentNeed = need.id === _.get(payment, 'need.id')
                            const isValidated = _.get(payment, 'status.id') === 'validated'
                            return isCurrentNeed && isValidated
                        })
                        .reduce((acc, payment) => {
                            return acc + Number(payment.proposedAmountInEuros || 0)
                        }, 0)
                    callback(null, total)
                }
            },
            {
                path: 'validatedAmountInKilograms',
                type: 'decimal',
                fieldPath: ['reliefFund.payments.status.id', 'reliefFund.payments.proposedAmountInKilograms', 'reliefFund.payments.need.id'],
                $f: function (need, context, callback) {
                    const total = need.reliefFund.payments
                        .filter(payment => {
                            const isCurrentNeed = need.id === _.get(payment, 'need.id')
                            const isValidated = _.get(payment, 'status.id') === 'validated'
                            return isCurrentNeed && isValidated
                        })
                        .reduce((acc, payment) => {
                            return acc + Number(payment.proposedAmountInKilograms || 0)
                        }, 0)
                    callback(null, total)
                }
            },
            {
                path: 'treatedAmountInEuros',
                type: 'decimal',
                fieldPath: ['reliefFund.payments.status.id', 'reliefFund.payments.proposedAmountInEuros', 'reliefFund.payments.need.id', 'reliefFund.payments.paymentDate'],
                $f: function (need, context, callback) {
                    const total = need.reliefFund.payments
                        .filter(payment => {
                            const isCurrentNeed = need.id === _.get(payment, 'need.id')
                            const isValidated = _.get(payment, 'status.id') === 'validated'
                            const havePaymentDate = !!payment.paymentDate
                            return isCurrentNeed && isValidated && havePaymentDate
                        })
                        .reduce((acc, payment) => {
                            return acc + Number(payment.proposedAmountInEuros || 0)
                        }, 0)
                    callback(null, total)
                }
            },
            {
                path: 'treatedAmountInKilograms',
                type: 'decimal',
                fieldPath: ['reliefFund.payments.status.id', 'reliefFund.payments.proposedAmountInKilograms', 'reliefFund.payments.need.id', 'reliefFund.payments.paymentDate'],
                $f: function (need, context, callback) {
                    const total = need.reliefFund.payments
                        .filter(payment => {
                            const isCurrentNeed = need.id === _.get(payment, 'need.id')
                            const isValidated = _.get(payment, 'status.id') === 'validated'
                            const havePaymentDate = !!payment.paymentDate
                            return isCurrentNeed && isValidated && havePaymentDate
                        })
                        .reduce((acc, payment) => {
                            return acc + Number(payment.proposedAmountInKilograms || 0)
                        }, 0)
                    callback(null, total)
                }
            },
        ],
        filters: [
            {
                name: 'byReliefFund',
                isDefault: false,
                query: (context) => {
                    const reliedFundId = _.get(context, 'data.reliefFund.id')
                    return reliedFundId
                        ? {reliefFund: new global.ObjectID(reliedFundId)}
                        : {_id: null}
                }
            },
            {
                name: 'hasAwardedAmount', //sollicitations acceptées
                isDefault: false,
                query: () => {
                    return {
                        $or: [
                            { awardedAmountInEuros: { $gt: 0 } },
                            { awardedAmountInKilograms: { $gt: 0 } }
                        ]
                    }
                }
            },
            {
                name: 'stillHaveSold',
                isDefault: false,
                async: true,
                query: async (context, callback) => {
                    const reliedFundId = _.get(context, 'data.reliefFund.id')
                    const needs = await global.app.C.Need.find({
                        ...basicContext(context),
                        fieldPath: [
                            'natureOfHelp.unit.id',
                            'reliefFund.payments.need.id',
                            'reliefFund.payments.status',
                            'reliefFund.payments.proposedAmountInEuros',
                            'reliefFund.payments.proposedAmountInKilograms'
                        ],
                        query: {reliefFund: new global.ObjectID(reliedFundId)}
                    })

                    const needsThatHaveSold = needs.filter(need => {
                        const paymentsForThisNeed = (need.reliefFund.payments || []).filter(payment => payment.need?.id === need.id && !['refused', 'draft', 'incomplete'].includes(_.get(payment, 'status.id')))
                        const noPaymentsForThisNeedYet = paymentsForThisNeed.length === 0
                        if (noPaymentsForThisNeedYet) return true
                        const unit = need.natureOfHelp.unit.id
                        const needPayments = paymentsForThisNeed
                            .reduce((acc, payment) => {
                                if (unit === 'kilogram'){
                                    if(!payment.proposedAmountInKilograms) return acc
                                    return acc + Number(payment.proposedAmountInKilograms || 0)
                                } else {
                                    if(!payment.proposedAmountInEuros) return acc
                                    return acc + Number(payment.proposedAmountInEuros || 0)
                                }
                            }, 0)
                        return need?.awardedAmountInEuros - needPayments > 0
                    })

                    return callback(null, {_id: { $in: needsThatHaveSold.map(need => new global.ObjectID(need.id)) }})
                }
            }
        ]
    },
    {
        name: 'ReliefFund',
        facets: [
            {name: 'files', path: 'associationUpdatedStatutes'},
            {name: 'files', path: 'associationStatusNotice'},
            {name: 'files', path: 'latestAssociationReport'},
            {name: 'files', path: 'insuranceContract'},
            {name: 'files', path: 'bankIdentityStatement'},
            {name: 'conversations'}
        ],
        fields: [
            {path: 'applicationNumber', type: 'string', unique: true},
            {type: 'Organization', nullable: true},
            {path: 'object', type: 'string'},
            {path: 'description', type: 'string'},
            {path: 'needs', type: 'Need', link: 'OTM'},
            {path: 'payments', type: 'Payment', link: 'OTM'},
            {path: 'associationInformationUpToDate', type: 'boolean'},
            {path: 'shelterInformationUpToDate', type: 'boolean'},
            {path: 'shelterCapacityInformationUpToDate', type: 'boolean'},
            /*
            {path: 'latestAssociationReportAvailable', type: 'boolean'},
            {path: 'associationIbanAndBicAvailable', type: 'boolean'},
            {path: 'associationStatusNoticeAvailable', type: 'boolean'},
            {path: 'associationUpdatedStatutesAvailable', type: 'boolean'},
             */
            {path: 'statementOfHonestyAndAccuracy', type: 'boolean'},
            {
                path: 'ongoingLiberalitiesSummary',
                fieldPath: ['organization.id'],
                $f: function (application, context, callback) {
                    getOngoingLiberalitiesSummary(_.get(application, 'organization.id'), context)
                        .then(summary => callback(null, summary))
                        .catch(callback)
                }
            },
            {
                path: 'ongoingLiberalities',
                type: 'boolean',
                fieldPath: ['ongoingLiberalitiesSummary'],
                f: function () {
                    return _.get(this, 'ongoingLiberalitiesSummary.filesCount', 0) > 0
                }
            },
            {
                path: 'ongoingLiberalitiesBenefactors',
                fieldPath: ['ongoingLiberalitiesSummary'],
                f: function () {
                    return _.get(this, 'ongoingLiberalitiesSummary.benefactors', '')
                }
            },
            {
                path: 'ongoingLiberalitiesPropertyTypes',
                fieldPath: ['ongoingLiberalitiesSummary'],
                f: function () {
                    return _.get(this, 'ongoingLiberalitiesSummary.propertyTypes', '')
                }
            },
            {
                path: 'ongoingLiberalitiesNetForecast',
                type: 'decimal',
                fieldPath: ['ongoingLiberalitiesSummary'],
                f: function () {
                    return _.get(this, 'ongoingLiberalitiesSummary.netForecast', 0)
                }
            },
            {
                path: 'liberalityAdvanceBalance',
                type: 'decimal',
                fieldPath: ['ongoingLiberalitiesSummary'],
                f: function () {
                    return _.get(this, 'ongoingLiberalitiesSummary.advanceBalance', 0)
                }
            },
            {
                path: 'ongoingLiberalitiesPaidAmount',
                type: 'decimal',
                fieldPath: ['ongoingLiberalitiesSummary'],
                f: function () {
                    return _.get(this, 'ongoingLiberalitiesSummary.paidAmount', 0)
                }
            },
            {path: 'currentSuccessionsName'},
            {path: 'estimatedAmount', type: 'integer', nullable: true},
            {path: 'studyCommitteeComment', type: 'string'},
            {path: 'decision', type: 'string'},
            {path: 'comment', type: 'string'},
            {path: 'status', type: 'ReliefFundApplicationStatus', nullable: true},
            {path: 'submitter', type: 'User', nullable: true},
            {path: 'controller', type: 'User', nullable: true},
            {path: 'validator', type: 'User', nullable: true},
            {
                path: "needsTypes",
                fieldPath: ['needs.nameWithUnit'],
                $f: function (application, context, callback) {
                    callback(null, application.needs.map(need => _.get(need, 'nameWithUnit', '')).join('; '));
                }
            },
            {
                path: "totalTotalAmountsInEuros",
                type: 'decimal',
                fieldPath: ['needs.totalAmountInEuros'],
                $f: function (application, context, callback) {
                    const total = application.needs.reduce((acc, need) => {
                        return acc + Number(need.totalAmountInEuros || 0)
                    }, 0)
                    callback(null, total);
                }
            },
            {
                path: "totalTotalAmountsInKilograms",
                type: 'decimal',
                fieldPath: ['needs.totalAmountInKilograms'],
                $f: function (application, context, callback) {
                    const total = application.needs.reduce((acc, need) => {
                        return acc + Number(need.totalAmountInKilograms || 0)
                    }, 0)
                    callback(null, total);
                }
            },
            {
                path: "totalRequestedAmountsInEuros",
                type: 'decimal',
                fieldPath: ['needs.requestedAmountInEuros'],
                $f: function (application, context, callback) {
                    const total = application.needs.reduce((acc, need) => {
                        return acc + Number(need.requestedAmountInEuros || 0)
                    }, 0)
                    callback(null, total);
                }
            },
            {
                path: "totalRequestedAmountsInKilograms",
                type: 'decimal',
                fieldPath: ['needs.requestedAmountInKilograms'],
                $f: function (application, context, callback) {
                    const total = application.needs.reduce((acc, need) => {
                        return acc + Number(need.requestedAmountInKilograms || 0)
                    }, 0)
                    callback(null, total);
                }
            },
            {
                path: "totalAwardedAmountsInEuros",
                type: 'decimal',
                fieldPath: ['needs.awardedAmountInEuros'],
                $f: function (application, context, callback) {
                    const total = application.needs.reduce((acc, need) => {
                        if(!need.awardedAmountInEuros) return acc
                        return acc + Number(need.awardedAmountInEuros || 0)
                    }, 0)
                    callback(null, total)
                }
            },
            {
                path: "totalAwardedAmountsInKilograms",
                type: 'decimal',
                fieldPath: ['needs.awardedAmountInKilograms'],
                $f: function (application, context, callback) {
                    const total = application.needs.reduce((acc, need) => {
                        if(!need.awardedAmountInKilograms) return acc
                        return acc + Number(need.awardedAmountInKilograms || 0)
                    }, 0)
                    callback(null, total)
                }
            },
            {
                path: 'totalProposedAmountsInEuros',
                type: 'decimal',
                fieldPath: ['payments.status.id', 'payments.proposedAmountInEuros'],
                $f: function (application, context, callback) {
                    const total = application.payments
                        .filter(payment => _.get(payment, 'status.id') !== 'refused')
                        .reduce((acc, payment) => {
                            return acc + Number(payment.proposedAmountInEuros || 0)
                        }, 0)
                    callback(null, total)
                }
            },
            {
                path: 'totalProposedAmountsInKilograms',
                type: 'decimal',
                fieldPath: ['payments.status.id', 'payments.proposedAmountInKilograms'],
                $f: function (application, context, callback) {
                    const total = application.payments
                        .filter(payment => _.get(payment, 'status.id') !== 'refused')
                        .reduce((acc, payment) => {
                            return acc + Number(payment.proposedAmountInKilograms || 0)
                        }, 0)
                    callback(null, total)
                }
            },
            {
                path: 'totalValidatedAmountsInEuros',
                type: 'decimal',
                fieldPath: ['payments.status.id', 'payments.proposedAmountInEuros'],
                $f: function (application, context, callback) {
                    const total = application.payments
                        .filter(payment => _.get(payment, 'status.id') === 'validated')
                        .reduce((acc, payment) => {
                            return acc + Number(payment.proposedAmountInEuros || 0)
                        }, 0)
                    callback(null, total)
                }
            },
            {
                path: 'totalValidatedAmountsInKilograms',
                type: 'decimal',
                fieldPath: ['payments.status.id', 'payments.proposedAmountInKilograms'],
                $f: function (application, context, callback) {
                    const total = application.payments
                        .filter(payment => _.get(payment, 'status.id') === 'validated')
                        .reduce((acc, payment) => {
                            return acc + Number(payment.proposedAmountInKilograms || 0)
                        }, 0)
                    callback(null, total)
                }
            },
            {
                path: 'totalTreatedAmountsInEuros',
                type: 'decimal',
                fieldPath: ['payments.status.id', 'payments.proposedAmountInEuros', 'payment.paymentDate'],
                $f: function (application, context, callback) {
                    const total = application.payments
                        .filter(payment => _.get(payment, 'status.id') === 'validated' && !!payment.paymentDate)
                        .reduce((acc, payment) => {
                            return acc + Number(payment.proposedAmountInEuros || 0)
                        }, 0)
                    callback(null, total)
                }
            },
            {
                path: 'totalTreatedAmountsInKilograms',
                type: 'decimal',
                fieldPath: ['payments.status.id', 'payments.proposedAmountInKilograms'],
                $f: function (application, context, callback) {
                    const total = application.payments
                        .filter(payment => _.get(payment, 'status.id') === 'validated' && !!payment.paymentDate)
                        .reduce((acc, payment) => {
                            return acc + Number(payment.proposedAmountInKilograms || 0)
                        }, 0)
                    callback(null, total)
                }
            },
            {
                path: "fundingRateForEuros",
                fieldPath: ["totalTotalAmountsInEuros", "totalRequestedAmountsInEuros"],
                $f: function (application, context, callback) {
                    const fundingRate = application.totalTotalAmountsInEuros > 0
                        ? _.round((application.totalRequestedAmountsInEuros / application.totalTotalAmountsInEuros) * 100)
                        : 0

                    callback(null, `${fundingRate}%`)
                }
            },
            {
                path: "fundingRateForKilograms",
                fieldPath: ["totalTotalAmountsInKilograms", "totalRequestedAmountsInKilograms"],
                $f: function (application, context, callback) {
                    const fundingRate = application.totalTotalAmountsInKilograms > 0
                        ? _.round((application.totalRequestedAmountsInKilograms / application.totalTotalAmountsInKilograms) * 100)
                        : 0

                    callback(null, `${fundingRate}%`)
                }
            },
            {
                path: "paymentRateInEuros",
                fieldPath: ["totalAwardedAmountsInEuros", "totalValidatedAmountsInEuros"],
                $f: function (application, context, callback) {
                    const fundingRate = application.totalAwardedAmountsInEuros > 0
                        ? _.round((application.totalValidatedAmountsInEuros / application.totalAwardedAmountsInEuros) * 100)
                        : 0

                    callback(null, `${fundingRate}%`)
                }
            },
            {
                path: "paymentRateInKilograms",
                fieldPath: ["totalAwardedAmountsInKilograms", "totalValidatedAmountsInKilograms"],
                $f: function (application, context, callback) {
                    const fundingRate = application.totalAwardedAmountsInKilograms > 0
                        ? _.round((application.totalValidatedAmountsInKilograms / application.totalAwardedAmountsInKilograms) * 100)
                        : 0

                    callback(null, `${fundingRate}%`)
                }
            },
            {
                path: "treatmentRateInEuros",
                fieldPath: ["totalAwardedAmountsInEuros", "totalTreatedAmountsInEuros"],
                $f: function (application, context, callback) {
                    const fundingRate = application.totalAwardedAmountsInEuros > 0
                        ? _.round((application.totalTreatedAmountsInEuros / application.totalAwardedAmountsInEuros) * 100)
                        : 0

                    callback(null, `${fundingRate}%`)
                }
            },
            {
                path: "treatmentRateInKilograms",
                fieldPath: ["totalAwardedAmountsInKilograms", "totalTreatedAmountsInKilograms"],
                $f: function (application, context, callback) {
                    const fundingRate = application.totalAwardedAmountsInKilograms > 0
                        ? _.round((application.totalTreatedAmountsInKilograms / application.totalAwardedAmountsInKilograms) * 100)
                        : 0

                    callback(null, `${fundingRate}%`)
                }
            },
            {
              path: 'organizationName',
              fieldPath: ['submitter.id'],
              $f: function (application, context, callback) {
                  const submitterId = _.get(application, 'submitter.id')
                  if(!submitterId) {
                      return callback(null, '')
                  }
                  global.app.C.CUser.get({kpUser: new global.ObjectID(submitterId)}, {
                      ...basicContext(context),
                      fieldPath: ['id', 'organizationName']
                  }, (error, cUser) => {
                      callback(error, cUser.organizationName)
                  })
              }
            },
            {
              path: 'organizationTradingName',
              fieldPath: ['submitter.id'],
              $f: function (application, context, callback) {
                  const submitterId = _.get(application, 'submitter.id')
                  if(!submitterId) {
                      return callback(null, '')
                  }
                  global.app.C.CUser.get({kpUser: new global.ObjectID(submitterId)}, {
                      ...basicContext(context),
                      fieldPath: ['id', 'organizationTradingName']
                  }, (error, cUser) => {
                      callback(error, cUser?.organizationTradingName)
                  })
              }
            },
            {
                path: 'fullName',
                fieldPath: ['applicationNumber', 'organizationName', 'object'],
                f: function () {
                    return `${this.applicationNumber} - ${this.organizationName} - ${this.object}`
                }
            },
            {
                path: "sequence",
                unique: true,
                ps: {
                    object: [{
                        type: "nextSequence",
                        sequenceId: "reliefFundApplicationSequence",
                        formatResult: function(result) {
                            return result.toString().padStart(4, '0') // Formats the sequence number, e.g., "0001"
                        }
                    }]
                }
            },
            {
                path: 'incompletePayments',
                nullable: true,
                fieldPath: ['status.id', 'payments.status.id'],
                $f: function(application, context, callback) {
                    const incompletePayments = application.payments?.filter(payment => _.get(payment, 'status.id') === 'incomplete').length

                    if(!incompletePayments) {
                        return callback(null, null)
                    }
                    return callback(
                        null,
                        {id: incompletePayments.toString(), number: incompletePayments, bStyle: 'warning', style: {padding: '0px 6px', float: 'right'}}
                    )
                }
            },
            {
                path: "buttons",
                $f: async function (application, context, callback) {
                    const buttons = await getButtonsForModule(application, context);
                    callback(null, buttons);
                }
            },
            {
                path: "noDeleteButtonAccess",
                $f: function (application, context, callback) {
                    // Check if the application status is NOT 'draft' or 'questioned'
                    const noDeleteAccess = !['draft', 'questioned'].includes(_.get(application, 'status.id'))

                    // Call the callback function with the result
                    // true means no delete access, false means delete access is allowed
                    callback(null, noDeleteAccess)
                }
            }
        ],
        filters: [
            {
                name: 'isAcceptedOrClosed',
                isDefault: false,
                query: () => ({status : {$in: ['accepted', 'closed'] }})
            },
            {
                name: 'isAccepted',
                isDefault: false,
                query: () => ({status : 'accepted'})
            },
            {
                name: 'userIsSubmitter',
                isDefault: false,
                query: context => {
                    const userId = _.get(context, 'user.id')
                    return userId ? {submitter: new global.ObjectID(userId)} : {_id: null}
                }
            },
            {
                name: 'hasOngoingPayment',
                isDefault: false,
                async: true,
                query: (context, callback) => {
                    global.app.C.Payment.collection.find({status: 'ongoing'})
                        .toArray()
                        .then((payments) => callback(null, {_id: {$in: payments.map(payment => payment.reliefFund)} }))
                        .catch(callback)
                }
            },
            {
                name: 'hasNonDraftPayment',
                isDefault: false,
                async: true,
                query: (context, callback) => {
                    global.app.C.Payment.collection.find({status: {$ne: 'draft'}})
                        .toArray()
                        .then(payments => callback(null, {_id: {$in: payments.map(payment => payment.reliefFund)} }))
                        .catch(e => callback(e))
                }
            },
            {
                name: 'userHasAuthorization',
                async: true,
                isDefault: false,
                query: async (context, callback) => {
                    const userId = _.get(context, 'user.id')
                    const moduleId = context.clientContext.moduleId
                    const authorizationMapping = {
                        'm-C-study': 'studyAuthorization',
                        'm-C-decision': 'decisionAuthorization',
                        'm-C-reliefFundPayment': 'solicitationsManagementAuthorization',
                        'm-C-reliefFundPaymentValidation': 'liberationAuthorization',
                        'm-C-followUp': ['studyAuthorization', 'decisionAuthorization', 'solicitationsManagementAuthorization', 'liberationAuthorization']
                    };

                    // Determine the authorization property based on the current module ID
                    const authorizationProperties = authorizationMapping[moduleId]
                    try {
                        const cUser = await global.app.C.CUser.get({kpUser: new global.ObjectID(userId)}, {
                            ...basicContext(context),
                            fieldPath: [
                                'id',
                                'functions.studyAuthorization',
                                'functions.decisionAuthorization',
                                'functions.solicitationsManagementAuthorization',
                                'functions.liberationAuthorization'
                            ]
                        })
                        const userHasAuthorization = cUser.functions.some(userFunction =>{
                            if( typeof authorizationProperties === 'string' ) {
                                return !!userFunction[authorizationProperties]
                            }
                            if( _.isArray(authorizationProperties) ) {
                                return authorizationProperties.some(authorizationProperty => !!userFunction[authorizationProperty])
                            }
                            return false
                        })
                        return callback(null,
                            moduleId === 'm-C-followUp'
                                ? userHasAuthorization ? {} : {submitter: new global.ObjectID(userId)}
                                : userHasAuthorization ? {} : {_id: null}
                    )
                    } catch (e) {
                        callback(e)
                    }

                }
            }
        ],
        ps: {
            context: [{
                $$u: function (context, callback) {
                    if (this.options.accessType === "S" && context.restAction && context.restAction.crudType === "C") {
                        context.internalFieldPath = [
                            ...new Set([
                                ...context.internalFieldPath,
                                "sequence" // Ensures 'sequence' is included for sequence generation on creation
                            ])
                        ];
                    }
                    callback(null, context)
                }
            }]
        },
        authorizationsValidation: function (cUser, context, callback) {
            const moduleId = context.clientContext.moduleId

            // Mapping module IDs to their corresponding authorization properties
            const authorizationMapping = {
                'm-C-submission': 'submissionAuthorization',
                'm-C-study': 'studyAuthorization',
                'm-C-decision': 'decisionAuthorization',
                'm-C-reliefFundPayment': 'solicitationsManagementAuthorization',
                'm-C-reliefFundPaymentValidation': 'liberationAuthorization'
            };

            // Determine the authorization property based on the current module ID
            const authorizationProperty = authorizationMapping[moduleId]

            if (authorizationProperty) {
                const hasAuthorization = cUser.functions.some(userFunction => !!userFunction[authorizationProperty])

                if (!hasAuthorization) {
                    // Adjust the error message based on the specific authorization failure
                    return callback(new Errors.ValidationError(`${authorizationProperty}Denied`))
                }
            }

            return callback()
        },
        checkboxesValidation: function (cUser, newObject, context, callback) {
            const moduleId = context.clientContext.moduleId
            const buttonAction = context.action

            //Mouad's guess here :
            //this case utility is to not block the save, the check must be done only when submitting to the next step
            //moduleId !== 'm-C-submission' : because we would have already checked in the previous modules

            const isHeadingToNextStep = ['submit', 'update'].includes(buttonAction)
            if(moduleId !== 'm-C-submission' || !isHeadingToNextStep) return callback()

            const checkboxesToValidate = [
                'associationInformationUpToDate',
                'statementOfHonestyAndAccuracy'
            ]
            if (!!cUser.organization?.hasShelters) {
                checkboxesToValidate.push('shelterInformationUpToDate')
                checkboxesToValidate.push('shelterCapacityInformationUpToDate')
            }
            let missingCheckbox
            checkboxesToValidate.some(checkbox => {
                const isMissing = !newObject[checkbox]
                if(isMissing) {
                    missingCheckbox = checkbox
                    return true
                }
            })

            if (!!missingCheckbox) return callback(new Errors.ValidationError(context.tc('checkboxIsRequired', {checkbox: context.tc(missingCheckbox)})))
            return callback()
        },
        checkUniqueNeedsByType: function (newObject, context, callback) {
            const natureOfHelpSet = new Set()
            const hasDuplicateNeed = newObject.needs.some(need => {
                if (natureOfHelpSet.has(need.natureOfHelp.id)) {
                    return true; // Duplicate found
                }
                natureOfHelpSet.add(need.natureOfHelp.id);
                return false;
            });

            if (hasDuplicateNeed) {
                return callback(new Errors.ValidationError(context.tc('needsMustBeUniqueByType')));
            }

            callback();
        },
        checkAllNeedsTypeAreActive: function (activeNeedTypes, newObject, context, callback) {
            const moduleId = context.clientContext.moduleId
            if(moduleId !== 'm-C-submission') return callback()

            const activeNeedTypesIds = activeNeedTypes.map(type => new global.ObjectID(type.id).toString());
            const filledNeedTypesIds = (newObject.needs || []).map(need => new global.ObjectID(need.natureOfHelp.id).toString());
            const allFilledAreActive = filledNeedTypesIds.every(id => activeNeedTypesIds.includes(id));

            if (!allFilledAreActive) {
                return callback(new Errors.ValidationError(context.tc('someNeedTypesAreNotActiveAnymore')));
            }

            callback();
        },
        checkNeedsNumbersConsistency: function (newObject, context, callback) {
            const moduleId = context.clientContext.moduleId
            const buttonAction = context.action
            const isHeadingToNextStep = ['transmitPayments'].includes(buttonAction)

            if (moduleId === 'm-C-reliefFundPayment' && isHeadingToNextStep) {
                const noEmptyProposedAmounts = newObject.payments.every(payment => {
                    return payment.proposedAmountInEuros > 0 || payment.proposedAmountInKilograms > 0
                })
                if (!noEmptyProposedAmounts){
                    return callback(new Errors.ValidationError(context.tc('allProposedAmountsMustBeStrictPositive')));
                }
            }
            const everyRequestedLessThanTotalNeeded = newObject.needs.every(need => {
                return need.totalAmountInEuros >= need.requestedAmountInEuros // requestedLessThanTotalNeeded
                    && need.totalAmountInKilograms >= need.requestedAmountInKilograms //requestedLessThanTotalNeeded
            });

            if (!everyRequestedLessThanTotalNeeded) {
                return callback(new Errors.ValidationError(context.tc('everyRequestedLessThanTotalNeeded')));
            }

            callback();
        },
        needsDecisionModuleValidation: function (newObject, context, callback) {
            const moduleId = context.clientContext.moduleId
            const buttonAction = context.action

            if(moduleId !== 'm-C-decision' || buttonAction !== 'accept') return callback()

            const exceedingNeed = newObject.needs.find(need => {
                const requested = need.natureOfHelp.unit.id === 'kilogram' ? need.requestedAmountInKilograms : need.requestedAmountInEuros
                return getAllocationsTotal(need) > Number(requested || 0)
            })
            if (exceedingNeed) {
                const unitName = _.get(exceedingNeed, 'natureOfHelp.unit.id') === 'kilogram' ? 'kg' : '€'
                const requested = unitName === 'kg' ? exceedingNeed.requestedAmountInKilograms : exceedingNeed.requestedAmountInEuros
                return callback(new Errors.ValidationError(context.tc('needAllocationsExceedRequested', {
                    needType: _.get(exceedingNeed, 'natureOfHelp.name'),
                    total: `${getAllocationsTotal(exceedingNeed)} ${unitName}`,
                    requested: `${requested || 0} ${unitName}`
                })))
            }

            const hasAwardedAmount = newObject.needs.some(need => getAllocationsTotal(need) > 0)
            if (!hasAwardedAmount) return callback(new Errors.ValidationError(context.tc('atLeastOneAwardedAmount')))

            return callback()
        },
        cannotModifyProcessedPayments: function (newObject, oldObject, context, callback) {
            const moduleId = context.clientContext.moduleId
            if(moduleId !== 'm-C-reliefFundPayment') return callback()

            const processedPayments = newObject.payments.filter(payment => ['validated', 'refused'].includes(payment.status?.id))
            if(processedPayments.length === 0) return callback()

            const hasModifiedProcessedPayment = processedPayments.some(payment => {
                const previousValues = oldObject.payments.find(previousValue => previousValue.id === payment.id)
                const allocationChanged = (_.get(previousValues, 'allocation.id') || _.get(previousValues, 'need.id')) !== (_.get(payment, 'allocation.id') || _.get(payment, 'need.id'))

                return getPaymentUnitId(payment) === 'kilogram' ?
                    allocationChanged
                    || previousValues.proposedAmountInKilograms !== payment.proposedAmountInKilograms
                    : allocationChanged
                    || previousValues.proposedAmountInEuros !== payment.proposedAmountInEuros
            })

            if(hasModifiedProcessedPayment) return callback(new Errors.ValidationError(context.tc('Vous ne pouvez pas modifier un paiement traité')))

            return callback()
        },
        checkNeedsBalance: function (newObject, oldObject, context, callback) {
            const moduleId = context.clientContext.moduleId
            if(moduleId !== 'm-C-reliefFundPayment' || context.action !== 'transmitPayments') return callback()
            // Suivi par couple « Type sollicitation - Forme d'aide » (repli sur le type pour les paiements non migrés)
            const groupedByAllocation = _.groupBy(newObject.payments, payment => (_.get(payment, 'allocation.id') || _.get(payment, 'need.id')))
            const negativeBalances = Object.keys(groupedByAllocation).map(allocationId => {
                const payments = groupedByAllocation[allocationId];
                const firstPayment = payments[0];
                const unit = _.get(firstPayment, 'allocation.need.natureOfHelp.unit') || firstPayment.need.natureOfHelp.unit //All payment should have same unit
                const awardedAmountInEurosOrKilograms = firstPayment.allocation
                    ? firstPayment.allocation.awardedAmount
                    : unit.id === 'euro' ? firstPayment.awardedAmountInEuros : firstPayment.awardedAmountInKilograms;
                const totalPaymentsInEurosOrKilograms = payments
                    .filter(payment => _.get(payment, 'status.id') !== 'refused')
                    .reduce((acc, payment) => acc + Number((unit.id === 'euro' ? payment.proposedAmountInEuros : payment.proposedAmountInKilograms) || 0), 0);
                const difference = _.round(totalPaymentsInEurosOrKilograms - Number(awardedAmountInEurosOrKilograms || 0), 2);

                if (difference > 0) {
                    return {
                        needType: _.get(firstPayment, 'allocation.fullName') || _.get(firstPayment, 'need.natureOfHelp.name', 'Type inconnu'),
                        unit,
                        totalPaymentsInEurosOrKilograms,
                        awardedAmountInEurosOrKilograms
                    };
                }
                return null;
            }).filter(Boolean); // Filtrer les cas où le solde est négatif

            // Si des soldes négatifs sont détectés, générer un message d'erreur
            if (negativeBalances.length > 0) {
                const errorMessages = negativeBalances.map(balance =>
                    `Le total à libérer (${balance.totalPaymentsInEurosOrKilograms} ${balance.unit.name}) pour "${balance.needType}" ne doit pas dépasser ce qui est accordé (${balance.awardedAmountInEurosOrKilograms} ${balance.unit.name}).`
                ).join(' ');

                return callback(new Errors.ValidationError(errorMessages));
            }
            return callback()
        },
        validateSave: async function (newObject, oldObject, context, callback) {
            let cUser, activeNeedTypes
            try {
                cUser = await global.app.C.CUser.get({kpUser: new global.ObjectID(context.user.id)}, {
                    ...basicContext(context),
                    fieldPath: [
                        'id',
                        'functions.id',
                        'functions.submissionAuthorization',
                        'functions.studyAuthorization',
                        'functions.decisionAuthorization',
                        'functions.solicitationsManagementAuthorization',
                        'functions.liberationAuthorization',
                        'organization.id',
                        'organization.hasShelters'
                    ]
                })
                activeNeedTypes = await global.app.C.NatureOfHelp.find({
                    ...basicContext(context),
                    query: {active: true}
                })
            } catch (e) {
                return callback(e)
            }
            context.data.organization = cUser.organization
            if (context.clientContext.moduleId === 'm-C-reliefFundPayment') {
                // Le paiement reste rattaché au type de sollicitation du couple « Type - Forme d'aide »
                newObject.payments.forEach(payment => {
                    if (payment.allocation?.need) payment.need = payment.allocation.need
                })
            }
            async.series([
                callback => this.authorizationsValidation(cUser, context, callback),
                callback => this.checkboxesValidation(cUser, newObject, context, callback),
                callback => this.checkUniqueNeedsByType(newObject, context, callback),
                callback => this.checkAllNeedsTypeAreActive(activeNeedTypes, newObject, context, callback),
                callback => this.needsDecisionModuleValidation(newObject, context, callback),
                callback => this.cannotModifyProcessedPayments(newObject, oldObject, context, callback),
                callback => this.checkNeedsBalance(newObject, oldObject, context, callback),
                callback => this.checkNeedsNumbersConsistency(newObject, context, callback)
            ], callback)
        },
        beforeSave: function(newObject, oldObject, context, callback) {

            if (context.restAction && context.restAction.crudType === "C") {
                // Initialize status to 'draft' for new records
                newObject.status = {id: 'draft'}
                // Prefix the applicationNumber with the current year
                const currentYear = new Date().getFullYear()
                newObject.applicationNumber = `FSE-${currentYear}-${newObject.sequence}`

                // set current user as submitter of the application
                newObject.submitter = _.pick(context.user, ['id', 'firstname', 'lastname', 'mail'])

                // set user organization
                newObject.organization = context.data.organization
            }

            const moduleId = context.clientContext.moduleId
            const buttonAction = context.action

            if (moduleId === 'm-C-decision') {
                // L'accordé du type de sollicitation est la somme des montants accordés par forme d'aide
                newObject.needs.forEach(need => {
                    need.allocations = (need.allocations || []).map(allocation => ({
                        ...allocation,
                        awardedAmount: Number(allocation.awardedAmount || 0) || 0
                    }))
                    const total = getAllocationsTotal(need)
                    const isKilogram = _.get(need, 'natureOfHelp.unit.id') === 'kilogram'
                    need.awardedAmountInEuros = isKilogram ? 0 : total
                    need.awardedAmountInKilograms = isKilogram ? total : 0
                })
            }

            if (moduleId === 'm-C-reliefFundPayment') {
                newObject.payments.forEach(payment => {
                    const oldPayment = (oldObject?.payments || []).find(p => p.id === payment.id)
                    const isValidated = _.get(payment, 'status.id') === 'validated'
                    const paymentDateJustFilled = payment.paymentDate && !moment(payment.paymentDate).isSame(moment(oldPayment?.paymentDate), 'day')

                    if (isValidated && paymentDateJustFilled) {
                        const unit = getPaymentUnitId(payment)
                        const commentText = `Libération effectuée : ${formatPaymentAmount(payment)}`

                        payment.comments = [
                            ...(payment.comments || []),
                            {
                                user: _.pick(context.user, ['id', 'name']),
                                text: commentText,
                                date: moment().format("YYYY-MM-DD HH:mm:ss")
                            }
                        ]
                    }
                })
            }

            // Ensure status does not change when the save button is clicked
            if (buttonAction === 'save') {
                return callback(null, newObject, oldObject)
            }

            newObject.lastComment = _.last(_.orderBy(newObject.conversations, 'timestamp'))?.text

            // Status transitions based on moduleId and button action
            switch (moduleId) {
                case 'm-C-submission':
                    if (buttonAction === 'submit') {
                        newObject.status.id = 'ongoing'
                    }
                    if(buttonAction === 'update') {
                        newObject.status.id = 'updated'
                    }
                    break
                case 'm-C-study':
                    if (buttonAction === 'examine') {
                        newObject.status.id = 'questioned'
                        newObject.controller = _.pick(context.user, ['id', 'name'])
                    } else if (buttonAction === 'accept') {
                        newObject.status.id = 'controlled'
                        newObject.controller = _.pick(context.user, ['id', 'name'])
                    }
                    break
                case 'm-C-decision':
                    if (buttonAction === 'accept') {
                        newObject.status.id = 'accepted'
                        newObject.validator = _.pick(context.user, ['id', 'name'])
                    } else if (buttonAction === 'refuse') {
                        newObject.status.id = 'refused'
                        newObject.validator = _.pick(context.user, ['id', 'name'])
                    } else if (buttonAction === 'examine') {
                        newObject.status.id = 'questioned'
                        newObject.validator = _.pick(context.user, ['id', 'name'])
                    }
                    break
                case 'm-C-reliefFundPayment':
                    if (buttonAction === 'closePayments') {
                        //refusing all remained non validated payments
                        newObject.payments.forEach(payment =>{
                            if (payment.status.id !== 'validated') payment.status.id = 'refused'
                        })
                        newObject.status.id = 'closed'
                    }
                    break
                case 'm-C-followUp':
                    newObject.conversations.push({
                        text: 'Pièces justificatives : Mises à jour',
                        sender: _.pick(context.user, ['id', 'name']),
                        seenBy: [context.user.id],
                        files: [],
                        timestamp: new Date().toISOString(),
                        from: 'system'
                    })
                    return callback(null, newObject, oldObject)
            }

            if(moduleId === 'm-C-reliefFundPayment' && buttonAction === 'transmitPayments') {
                newObject.payments = newObject.payments.map(
                    payment => {
                        const statusId = _.get(payment, 'status.id')
                        const commentText = statusId === 'draft'
                            ? `Proposition transmise : ${formatPaymentAmount(payment)}`
                            : `Proposition justifiée : ${formatPaymentAmount(payment)}`

                        if(['draft', 'incomplete'].includes(statusId)) {
                            return {
                                ...payment,
                                status: {
                                    id: 'ongoing'
                                },
                                comments: [
                                    ...payment.comments,
                                    {
                                        user: _.pick(context.user, ['id', 'name']),
                                        text: commentText,
                                        date: moment().format("YYYY-MM-DD HH:mm:ss")
                                    }
                                ]
                            }
                        }
                        return payment
                    }
                )
            }

            const statusObject = refundApplicationStatusObjects.find(status => status.id === newObject.status.id)

            if (!['transmitPayments', 'sendMessage'].includes(buttonAction)) {
                newObject.conversations.push({
                    text: `${statusObject.name}`,
                    sender: _.pick(context.user, ['id', 'name']),
                    seenBy: [context.user.id],
                    files: [],
                    timestamp: new Date().toISOString(),
                    from: 'system'
                })
            }

            if (moduleId === 'm-C-decision' && buttonAction === 'accept') {
                // Conditions de l'aide accordée, enregistrées pour le compte du décideur
                newObject.conversations.push({
                    text: buildAwardSummaryComment(newObject.needs),
                    sender: _.pick(context.user, ['id', 'name']),
                    seenBy: [context.user.id],
                    files: [],
                    timestamp: new Date().toISOString(),
                    from: 'user'
                })
            }

            // Proceed with saving
            callback(null, newObject, oldObject)
        },
        afterSave: async function (newObject, oldObject, context, callback) {
            const buttonAction = context.action
            const moduleId = context.clientContext.moduleId

            if (buttonAction === 'save') {
                return callback()
            }

            let mails = []

            //Prepare email to notify the submitter
            const notificationEmail = prepareNotificationEmail(newObject, context)

            if(notificationEmail) mails.push(notificationEmail)

            if(['submit', 'update'].includes(buttonAction)) {
                //Prepare controllers emails
                const controllersEmails = await prepareRoleBasedNotificationEmail("studyAuthorization", newObject, context)
                controllersEmails.forEach(mail => mails.push(mail))
            }

            if(moduleId === 'm-C-followUp' && buttonAction === 'updateDocuments') {
                //Prepare validators emails
                const controllersEmails = await prepareRoleBasedNotificationEmail("solicitationsManagementAuthorization", newObject, context)
                controllersEmails.forEach(mail => mails.push(mail))
            }

            getCNDANotificationData(context.group.id)
                .then(data => global.ioSocket.emit('afterSave', data))

            return global.mailer.sendMail(mails, (error) => {
                console.log(error)
                return callback()
            })
        }
    }
]

function getPaymentUnitId(payment) {
    return _.get(payment, 'allocation.need.natureOfHelp.unit.id') || _.get(payment, 'need.natureOfHelp.unit.id')
}

function getAllocationsTotal(need) {
    return _.round(_.sumBy(need.allocations || [], allocation => Number(allocation.awardedAmount || 0) || 0), 2)
}

function formatPaymentAmount(payment) {
    const unitId = getPaymentUnitId(payment)
    return formatReliefFundAmount(unitId === 'kilogram' ? payment.proposedAmountInKilograms : payment.proposedAmountInEuros, unitId)
}

// Synthèse de l'aide accordée : une ligne par sollicitation et par forme d'aide dont l'accordé est > 0
function buildAwardSummaryComment(needs) {
    const needItems = (needs || [])
        .filter(need => getAllocationsTotal(need) > 0)
        .map(need => {
            const unitId = _.get(need, 'natureOfHelp.unit.id')
            const requested = unitId === 'kilogram' ? need.requestedAmountInKilograms : need.requestedAmountInEuros
            const needName = `${_.get(need, 'natureOfHelp.name', '')} (${unitId === 'kilogram' ? 'kg' : '€'})`
            const allocationItems = need.allocations
                .filter(allocation => Number(allocation.awardedAmount || 0) > 0)
                .map(allocation => `<li>${reliefFundFormLabels[_.get(allocation, 'form.id')] || ''} : ${formatReliefFundAmount(allocation.awardedAmount, unitId)}</li>`)
                .join('')
            return `<li>${needName} : ${formatReliefFundAmount(requested, unitId)} sollicité<ul><li>Aide accordée :<ul>${allocationItems}</ul></li></ul></li>`
        })
        .join('')
    return `<ul>${needItems}</ul>`
}

function getNotificationEmailContent(newObject, context) {
    const buttonAction = context.action
    const moduleId = context.clientContext.moduleId
    switch (buttonAction) {
        case 'submit':
            return {
                subject: `CNDA - Dossier Fonds de Secours Exceptionnel N° ${newObject.applicationNumber}, Enregistré`,
                template: 'user_submission_notification.html'
            }
        case 'update':
            return {
                subject: `CNDA - Dossier Fonds de Secours Exceptionnel N° ${newObject.applicationNumber}, Mis à jour`,
                template: 'user_update_notification.html'
            }
        case 'examine':
            return {
                subject: `CNDA - Dossier Fonds de Secours Exceptionnel N° ${newObject.applicationNumber}, Compléments attendus`,
                template: 'user_examination_notification.html'
            }
        case 'refuse':
            return {
                subject: `CNDA - Dossier Fonds de Secours Exceptionnel N° ${newObject.applicationNumber}, Refusé`,
                template: 'user_refusal_notification.html'
            }
        case 'sendMessage':
            return {
                subject: `CNDA - Dossier Fonds de Secours Exceptionnel N° ${newObject.applicationNumber}, Suivi dossier mis à jour`,
                template: 'user_received_message_notification.html'
            }
        case 'accept':
            if(moduleId === 'm-C-study') {
                return {
                    subject: `CNDA - Dossier Fonds de Secours Exceptionnel N° ${newObject.applicationNumber}, Décision attendue`,
                    template: 'user_controller_acceptance_notification.html'
                }
            } else if(moduleId === 'm-C-decision') {
                return {
                    subject: `CNDA - Dossier Fonds de Secours N° ${newObject.applicationNumber}, Accepté`,
                    template: 'user_validator_acceptance_notification.html'
                }
            }
            break
    }
    return undefined
}

function prepareNotificationEmail(newObject, context) {
    const isCreationOperation = context.restAction && context.restAction.crudType === "C"
    const defaultMail = prepareDefaultMail()
    const content = getNotificationEmailContent(newObject, context)

    if(!content) return undefined

    return _.defaults({
        to: isCreationOperation ? newObject.submitter.mail : decrypt(newObject.submitter.mail),
        content: content.template,
        subject: { template: content.subject },
        context: {
            firstname: newObject.submitter.firstname,
            applicationNumber: newObject.applicationNumber,
            lastComment: newObject.lastComment,
            decision: newObject.decision,
            groupModelId: context.groupModel.id,
            baseUrl: global.isProd
                ? `https://${context.host}`
                : `http://localhost:3000`
        }
    }, defaultMail)
}

async function prepareRoleBasedNotificationEmail(authorization, newObject, context) {
    const recipients = await getUsersWithAuthorization(authorization)
    const defaultMail = prepareDefaultMail()
    const buttonAction = context.action
    const moduleId = context.clientContext.moduleId

    const content = {}

    switch (authorization) {
        case 'studyAuthorization':
            if(buttonAction === 'submit') {
                content.template = 'controller_submission_notification.html'
                content.subject = `CNDA - Dossier Fonds de Secours Exceptionnel N° ${newObject.applicationNumber}, Enregistré`
            }
            if(buttonAction === 'update') {
                content.template = 'controller_update_notification.html'
                content.subject = `CNDA - Dossier Fonds de Secours Exceptionnel N° ${newObject.applicationNumber}, Mis a jour`
            }
            break
        case 'decisionAuthorization':
            if(moduleId === 'm-C-study') {
                content.template = 'validator_control_notification.html'
                content.subject = `Notification de contrôle`
            }
            if(moduleId === 'm-C-reliefFundPayment') {
                content.template = 'validator_payment_notification.html'
                content.subject = `Notification de saisie de paiements`
            }
            break
        case 'solicitationsManagementAuthorization':
            if(buttonAction === 'updateDocuments') {
                content.template = 'documents_update_notification.html'
                content.subject = `CNDA - Dossier Fonds de Secours Exceptionnel N° ${newObject.applicationNumber}, Suivi dossier mis à jour`
            }
    }

    return recipients.map(recipient => {
        return _.defaults(
            {
                to: recipient.mail && decrypt(recipient.mail),
                content: content.template,
                subject: { template: content.subject },
                context: {
                    firstname: recipient.firstname,
                    applicationNumber: newObject.applicationNumber,
                    organizationName: newObject.organization?.name,
                    groupModelId: context.groupModel.id,
                    baseUrl: global.isProd
                        ? `https://${context.host}`
                        : `http://localhost:3000`
                }
            },
            defaultMail
        )
    })
}
